import React from "react";
import PropTypes from "prop-types";
import Yup from "yup";
import { Form, Formik } from "formik";
import get from "lodash/get";
import has from "lodash/has";
import { withStyles } from "@mui/styles";
import {
  MenuItem,
  TextField,
  Select,
  Button,
  FormControl,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SimpleSnackbar from "common/components/SimpleSnackbar";
import Resource from "common/components/Resource";
import { FORM_SUBMISSION_ERROR } from "common/constants";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Community name is required"),
  city: Yup.number().notOneOf([0], "City is required").required(),
  multilingual: Yup.string().required("Multiligual name is required"),
});

const CommunitiesForm = ({
  classes,
  handleSubmit,
  initialValues,
  isEdit,
  disableLangPref,
}) => (
  <Formik
    enableReinitialize
    validationSchema={validationSchema}
    onSubmit={handleSubmit}
    initialValues={initialValues}
  >
    {({ values, errors, touched, isSubmitting, handleChange, status }) => (
      <Form>
        <Grid container direction="column" spacing={2}>
          <Grid size={{ xs: 12 }}>
            <FormControl
              className={classes.formControl}
              error={touched.city && has(errors, "city")}
            >
              <Resource
                name="cities"
                queryParams={{ pagination: false }}
                render={(cities) => (
                  <Select
                    name="city"
                    id="city"
                    value={values.city}
                    onChange={handleChange}
                  >
                    {cities.map((city) => (
                      <MenuItem value={city.id} key={city.id}>
                        {city.name}, {city.region}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {touched.city && has(errors, "city") ? (
                <FormHelperText>{errors.city}</FormHelperText>
              ) : (
                <FormHelperText>City</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl className={classes.formControl}>
              <TextField
                error={touched.name && has(errors, "name")}
                id="name"
                type="name"
                label="Name"
                margin="normal"
                value={values.name}
                onChange={handleChange}
                helperText={touched.name && get(errors, "name")}
              />
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }} className={classes.selectorGrid}>
            <FormControl className={classes.formControl}>
              <Select
                name="multilingual"
                id="multilingual"
                value={values.multilingual}
                disabled={disableLangPref}
                onChange={handleChange}
              >
                <MenuItem value={false} key="English">
                  English
                </MenuItem>
                <MenuItem value={true} key="Bilingual">
                  Bilingual
                </MenuItem>
              </Select>
              <FormHelperText>Language Preference</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.button}
            disabled={isSubmitting}
          >
            {isEdit ? "Update Neighborhood" : "Create Neighborhood"}
          </Button>
          {has(errors, "http") && (
            <SimpleSnackbar message={FORM_SUBMISSION_ERROR} />
          )}
        </Grid>
        {get(status, "success") && (
          <SimpleSnackbar
            message={
              isEdit
                ? "Neighborhood updated!"
                : "New neighborhood successfully created!"
            }
          />
        )}
      </Form>
    )}
  </Formik>
);

const styles = (theme) => ({
  button: {
    marginTop: theme.spacing(2),
  },
  paper: {
    paddingTop: 16,
    paddingBottom: 16,
    marginTop: theme.spacing(3),
    width: "80%",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 180,
  },
  selectorGrid: {
    marginTop: 28,
  },
});

CommunitiesForm.propTypes = {
  classes: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
  disableLangPref: PropTypes.bool,
};

export default withStyles(styles)(CommunitiesForm);
